'use client';

import type React from 'react';

import DefaultButton from '@/components/common/default/DefaultButton';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useCallback, useRef, memo } from 'react';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';

interface FilterSectionProps {
  id: string;
  title: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: (itemName?: string) => void;
  children: React.ReactNode;
  hasActiveFilters?: boolean;
  isLoading?: boolean;
  className?: string;
  secondaryAccentColor?: string;
}

const FilterSection = memo<FilterSectionProps>(
  ({
    id,
    title,
    icon,
    isOpen,
    onToggle,
    children,
    hasActiveFilters = false,
    isLoading = false,
    className = '',
    secondaryAccentColor = 'tw-bg-platinum-gray/50',
  }) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const { translate } = useTypedTranslation<CommonEnum>('common');

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      },
      [onToggle]
    );

    return (
      <div
        ref={sectionRef}
        className={`tw-border-b tw-border-platinum-gray/50 last:tw-border-b-0 ${className}`}
      >
        <DefaultButton
          id={`filter-section-${id}-button`}
          type="button"
          onClick={() => onToggle()}
          onKeyDown={handleKeyDown}
          className="tw-hover:tw-bg-platinum-gray/20 tw-flex tw-w-full tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-text-left tw-transition-all tw-duration-200 focus:tw-bg-slate-50/50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-slate-400"
          aria-expanded={isOpen}
          aria-controls={`filter-section-${id}`}
          aria-label={`${title} filter section${hasActiveFilters ? ' (has active filters)' : ''}`}
        >
          <div className="tw-flex tw-items-center tw-gap-3">
            <div
              className={`tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-p-2 tw-transition-colors group-hover:tw-opacity-80 ${secondaryAccentColor}`}
            >
              {icon}
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-font-semibold tw-text-textBlack">{title}</span>
              {hasActiveFilters && (
                <div className="tw-flex tw-h-2.5 tw-w-2.5 tw-items-center tw-justify-center tw-rounded-full tw-bg-gradient-to-r tw-from-order tw-to-custom-gold tw-shadow-sm">
                  <span className="tw-sr-only">
                    {translate(CommonEnum.has_active_filters) || 'Has active filters'}
                  </span>
                </div>
              )}
              {isLoading && <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin tw-text-slate-400" />}
            </div>
          </div>
          <div className="tw-rounded-lg tw-p-1 tw-transition-colors group-hover:tw-bg-platinum-gray/30">
            {isOpen ? (
              <ChevronDown className="tw-h-5 tw-w-5 tw-text-slate-gray tw-transition-transform" />
            ) : (
              <ChevronRight className="tw-h-5 tw-w-5 tw-text-slate-gray tw-transition-transform" />
            )}
          </div>
        </DefaultButton>
        {isOpen && (
          <section
            id={`filter-section-${id}`}
            className="tw-transition-all tw-duration-200"
            aria-labelledby={`filter-section-${id}-button`}
          >
            {children}
          </section>
        )}
      </div>
    );
  }
);

FilterSection.displayName = 'FilterSection';

export default FilterSection;
